'use client'

import BackArrowIcon from '@/assets/icons/back-arrow.svg'
import { useBlockCloseWindow } from '@/core/guards'
import { dateService } from '@/core/services/date'
import { Alert } from '@/core/ui/atoms/alert'
import { Button } from '@/core/ui/atoms/button'
import { FormInput } from '@/core/ui/forms/input'
import { FormRadioGroup, Radio } from '@/core/ui/forms/radio'
import { FormTextArea } from '@/core/ui/forms/text-area'
import { WAITING_TIME_AFTER_PICKUP } from '@/features/reservation/constants'
import { Divider } from '@heroui/divider'
import { useTranslations } from 'next-intl'
import { FormProvider } from 'react-hook-form'
import { MyDetails } from './my-details'
import { OtherPassengerDetails } from './other-passenger-details'
import { FormTimeAdjustment } from './time-adjustment/index.'
import { useCreateTrip } from './use-create-trip'
import { VehicleSizeRadio } from './vehicle-size-radio'

type Props = {
  showUserInfo?: boolean
  showSomeoneElseOption?: boolean
  isUpdate?: boolean
  isProvider?: boolean
}

export const CreateTrip: React.FC<Props> = ({
  showUserInfo,
  showSomeoneElseOption,
  isUpdate = false,
  isProvider = false,
}) => {
  const t = useTranslations()

  const {
    tripInfo,
    onSubmit,
    isPending,
    form,
    isAirport,
    time,
    handleVehicleSizeChange,
    error,
  } = useCreateTrip({
    isUpdate,
    isProvider,
  })

  useBlockCloseWindow({
    hasUnsavedChanges: true,
  })

  const tripTime = time instanceof Date ? time : dateService.timeToDate(time)

  return (
    <article>
      <h2 className='text-2xl font-medium'>{t('trip.detailsTitle')}</h2>
      <FormProvider {...form}>
        <form className='flex w-full flex-col gap-4' onSubmit={onSubmit}>
          <div className='my-3 flex flex-col gap-2'>
            <label htmlFor='vehicleType'>{t('forms.vehicleType')}</label>
            <p className='text-sm font-light'>{t('forms.vehicleTypeDesc')}</p>
            <div className='my-3 flex flex-col flex-wrap items-stretch gap-4 min-[28rem]:flex-row min-[28rem]:items-start'>
              <FormRadioGroup
                control={form.control}
                name='vehicleSizeId'
                orientation='horizontal'
                onSelectValue={handleVehicleSizeChange}
              >
                {tripInfo?.listVehicle.map(vehicle => (
                  <VehicleSizeRadio
                    key={vehicle.id}
                    value={vehicle.id.toString()}
                  >
                    {vehicle.name}
                  </VehicleSizeRadio>
                ))}
              </FormRadioGroup>
            </div>
          </div>
          <div className='flex w-full flex-col gap-3 sm:w-[28rem]'>
            <FormInput
              control={form.control}
              label={t('forms.flightNumberLabel')}
              labelClassName=''
              name='flightNumber'
              placeholder={t('forms.flightNumberPH')}
            />
            <p className='text-grey-60 text-xs'>
              {t('forms.flightNumberDesc')}
            </p>
          </div>
          <div className='hidden flex-1 sm:block'></div>
          {isAirport ? (
            <FormTimeAdjustment
              name='pickupTime'
              control={form.control}
              className='mx-3'
              start={tripTime}
              end={dateService.addHours(tripTime, WAITING_TIME_AFTER_PICKUP)}
            />
          ) : (
            <input {...form.register('pickupTime')} hidden />
          )}

          <Divider className='mb-3' />

          {showUserInfo && (
            <>
              {showSomeoneElseOption && (
                <FormRadioGroup
                  control={form.control}
                  name='tripFor'
                  color='danger'
                >
                  <Radio classNames={{ label: 'text-grey' }} value='forMe'>
                    {t('forms.forMe')}
                  </Radio>

                  <Radio
                    classNames={{ label: 'text-grey' }}
                    value='forSomeoneElse'
                  >
                    {t('forms.forSomeone')}
                  </Radio>
                </FormRadioGroup>
              )}
              {form.watch('tripFor') === 'forMe' ? (
                <MyDetails className='mt-5' />
              ) : (
                <OtherPassengerDetails className='mt-5' />
              )}
            </>
          )}
          <FormTextArea
            control={form.control}
            label={t('forms.noteLabel')}
            labelClassName='text-grey text-sm font-medium'
            name='note'
            placeholder={t('forms.notePH')}
            type='textarea'
            rows={3}
          />

          <input {...form.register('waitingTime')} type='text' hidden />
          <Alert error={error} />

          <Button
            type='submit'
            className='flex h-11 w-64 items-center gap-3 px-7 font-medium min-[28rem]:self-start'
            loading={isPending}
            startContent={
              <BackArrowIcon className='mt-0.5 rotate-180 rtl:rotate-0' />
            }
          >
            {t('actions.pay')}
          </Button>
        </form>
      </FormProvider>
    </article>
  )
}
